/**
 * SubscriptionRpcClient - AtomRpc integration for subscription endpoints
 *
 * This uses AtomRpc.Tag to create a special context tag that provides
 * proper integration with the atom runtime.
 *
 * Used for subscription management, checkout sessions, and customer portal.
 *
 * The ClerkTokenGetter layer reads the `__session` cookie directly (see
 * `readClerkSessionCookieToken`), falling back to `globalThis.__clerkSession`
 * when the cookie isn't present.
 *
 * @example
 * ```typescript
 * const subscriptionAtom = SubscriptionRpcClient.runtime.atom((get) =>
 *   Effect.gen(function*() {
 *     const client = yield* SubscriptionRpcClient;
 *     const subscription = yield* client("findSubscription", {});
 *     return subscription;
 *   })
 * );
 * ```
 *
 * @since 1.0.0
 */
import { AtomRpc } from "@effect-atom/atom-react";
import { FetchHttpClient } from "@effect/platform";
import { RpcSerialization } from "@effect/rpc";
import { SubscriptionRpcs } from "@fm/api/rpc";
import { Layer } from "effect";
import { makeRpcClientLayer, makeTokenGetterLayer } from "./shared.ts";

export class SubscriptionRpcClient extends AtomRpc.Tag<SubscriptionRpcClient>()(
  "SubscriptionRpcClient",
  {
    group: SubscriptionRpcs,
    protocol: makeRpcClientLayer(SubscriptionRpcs).pipe(
      Layer.provide(RpcSerialization.layerNdjson),
      Layer.provide(makeTokenGetterLayer()),
      Layer.provide(FetchHttpClient.layer),
    ),
  },
) {}
